import { Info as InfoIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { EntityDetailResponse } from "../../../../types/entityTypes";
import {
  getColorForStatus,
  getDisplayNameFromDbValue,
} from "../../../../utils/organizationStatusMapping";
import ConfidenceIndicator from "../../../common/ConfidenceIndicator";
import SocialMediaIcons from "../../../common/SocialMediaIcons";

interface OrganizationDetailProps {
  detailedOrganization: EntityDetailResponse;
  onEntityClick?: (entityId: string) => void;
}

const OrganizationDetail: React.FC<OrganizationDetailProps> = ({
  detailedOrganization,
  onEntityClick,
}) => {
  return (
    <Box sx={{ p: 0, flexShrink: 0 }}>
      <Grid container spacing={3}>
        {/* Company Overview Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 3,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", mb: 3 }}
            >
              Company Overview
            </Typography>
            <Grid container spacing={2}>
              {/* <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      Industry
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {}
                    </Typography>
                  </Grid> */}
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Home Country
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedOrganization?.o?.home_country || "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Websites
                </Typography>
                <Box sx={{ fontWeight: 500 }}>
                  {detailedOrganization?.o?.websites &&
                  detailedOrganization.o.websites.length > 0
                    ? detailedOrganization.o.websites
                        .slice(0, 5)
                        .map((website, index) => (
                          <Box key={index}>
                            <a
                              href={"https://" + website}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "inherit",
                                textDecoration: "underline",
                              }}
                            >
                              {website}
                            </a>
                          </Box>
                        ))
                    : "-"}
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Social Media
                </Typography>
                <Box sx={{ fontWeight: 500 }}>
                  {detailedOrganization?.o?.directories &&
                  detailedOrganization.o.directories.length > 0 ? (
                    <SocialMediaIcons
                      directories={detailedOrganization.o.directories}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      -
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Founding Year
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedOrganization?.o?.founding_year || "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Employees
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedOrganization?.o?.number_of_employees?.toLocaleString() ||
                    "-"}
                </Typography>
              </Grid>
              {/* <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {detailedOrganization?.f?.address}
                    </Typography>
                  </Grid> */}
              {/* <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      Coordinates
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {detailedOrganization?.f?.latitude && detailedOrganization?.f?.longitude
                        ? `${detailedOrganization.f.latitude.toFixed(4)}, ${detailedOrganization.f.longitude.toFixed(4)}`
                        : '-'
                      }
                    </Typography>
                  </Grid> */}
              {/* <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      Confidence Level
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {detailedOrganization?.o?.confidence_level || '-'}
                    </Typography>
                  </Grid> */}
              <Grid item xs={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Confidence Level
                  </Typography>
                  <Tooltip
                    title="The confidence level is calculated based on the number of sources we have for this entity and how recent the data is. More sources and fresher data result in higher confidence."
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      sx={{
                        padding: 0,
                        width: 16,
                        height: 16,
                        "& .MuiSvgIcon-root": { fontSize: 14 },
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ mt: 1 }}>
                  {detailedOrganization?.o?.confidence_level !== null &&
                  detailedOrganization?.o?.confidence_level !== undefined ? (
                    <ConfidenceIndicator
                      confidence={detailedOrganization.o.confidence_level}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      -
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Data Sources
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedOrganization?.o?.n_sources || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Operational Status Section */}
        {((detailedOrganization?.o?.status_labels?.length || 0) > 0 ||
          detailedOrganization?.o?.status_summary ||
          detailedOrganization?.o?.status_last_update_date) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Operational Status
              </Typography>

              {/* Status Information - Inline Layout */}
              <Grid container spacing={2}>
                {/* Status Summary - 2/4 of the grid */}
                {detailedOrganization?.o?.status_summary && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status Summary:
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {detailedOrganization.o.status_summary}
                    </Typography>
                  </Grid>
                )}

                {/* Status Labels - 1/4 of the grid */}
                {detailedOrganization?.o?.status_labels &&
                  detailedOrganization.o.status_labels.length > 0 && (
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        Current Status:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {detailedOrganization.o.status_labels.map(
                          (status, index) => {
                            const displayName = (() => {
                              try {
                                return getDisplayNameFromDbValue(status);
                              } catch {
                                return status;
                              }
                            })();
                            return (
                              <Chip
                                key={index}
                                label={displayName}
                                color={getColorForStatus(status)}
                                variant="outlined"
                                size="small"
                              />
                            );
                          }
                        )}
                      </Box>
                    </Grid>
                  )}

                {/* Last Updated Date - 1/4 of the grid */}
                {detailedOrganization?.o?.status_last_update_date && (
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {detailedOrganization.o.status_last_update_date}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Ownership Information Section */}
        {(() => {
          // Filter organizations for ownership relationships - same logic as Organizations tab
          const validOwners =
            detailedOrganization?.organizations?.filter(
              (item) => item.organization && item.organization.id
            ) || [];

          // Further filter for ownership relationships
          const ownerOrgs = validOwners.filter(
            (item) =>
              (item.relationship?.semantic_role === "Owner" ||
                item.relationship?.semantic_role === "Owner (JV)" ||
                item.relationship?.semantic_role === "Owner (Majority)" ||
                item.relationship?.semantic_role === "Owner (Partial)") &&
              item.relationship?.relation_status === "active"
          );

          // Fallback: if no specific owners found, show first few organizations as potential owners
          const displayOrgs = ownerOrgs.length > 0 ? ownerOrgs : [];

          return displayOrgs.length > 0 ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "background.paper",
                  p: 3,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "primary.main", mb: 3 }}
                >
                  Ownership & Control
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {displayOrgs.slice(0, 4).map((ownerItem, index) => {
                    const ownershipPercentage =
                      ownerItem.relationship?.ownership_share;
                    const ownerName =
                      ownerItem.organization?.name || "Unknown Owner";

                    return (
                      <Chip
                        key={index}
                        label={
                          ownershipPercentage &&
                          typeof ownershipPercentage === "number"
                            ? `${ownerName} (${(() => {
                                try {
                                  const share = ownershipPercentage;
                                  if (share <= 1.0) {
                                    return `${(share * 100).toFixed(1)}%`;
                                  } else {
                                    return `${share.toFixed(1)}%`;
                                  }
                                } catch (e) {
                                  return String(ownershipPercentage);
                                }
                              })()})`
                            : ownerName
                        }
                        onClick={() =>
                          onEntityClick?.(ownerItem.organization?.id || "")
                        }
                        onMouseDown={(event) => {
                          if (event.button === 1) {
                            event.preventDefault();
                            window.open(
                              `/entity/${ownerItem.organization?.id}`,
                              "_blank"
                            );
                          }
                        }}
                        variant="outlined"
                        size="small"
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            borderColor: "white",
                          },
                        }}
                      />
                    );
                  })}

                  {displayOrgs.length > 4 && (
                    <Tooltip
                      title={
                        <Box>
                          {displayOrgs.slice(4).map((org, idx) => (
                            <div key={idx}>{org.organization.name}</div>
                          ))}
                        </Box>
                      }
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ cursor: "help" }}
                      >
                        +{displayOrgs.length - 4} more
                      </Typography>
                    </Tooltip>
                  )}

                  {/* <Button
                        size="small"
                        onClick={() => setActiveTab(0)} // Switch to Organizations tab
                        sx={{ ml: 1, textTransform: 'none' }}
                      >
                        View All Relationships →
                      </Button> */}
                </Box>
              </Box>
            </Grid>
          ) : null;
        })()}

        {/* Business Operations Section */}
        {((detailedOrganization?.o?.services?.length || 0) > 0 ||
          (detailedOrganization?.o?.products?.length || 0) > 0) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Business Operations
              </Typography>

              {/* Services */}
              {detailedOrganization?.o?.services &&
                detailedOrganization.o.services.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Services Offered:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedOrganization.o.services
                        .slice(0, 6)
                        .map((service, index) => (
                          <Chip
                            key={index}
                            label={service}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedOrganization.o.services.length > 6 && (
                        <Tooltip
                          title={
                            <Box>
                              {detailedOrganization.o.services
                                .slice(6)
                                .map((service, idx) => (
                                  <div key={idx}>{service}</div>
                                ))}
                            </Box>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, alignSelf: "center", cursor: "help" }}
                          >
                            +{detailedOrganization.o.services.length - 6} more
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}

              {/* Products */}
              {detailedOrganization?.o?.products &&
                detailedOrganization.o.products.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Products Produced:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedOrganization.o.products
                        .slice(0, 6)
                        .map((product, index) => (
                          <Chip
                            key={index}
                            label={product}
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedOrganization.o.products.length > 6 && (
                        <Tooltip
                          title={
                            <Box>
                              {detailedOrganization.o.products
                                .slice(6)
                                .map((product, idx) => (
                                  <div key={idx}>{product}</div>
                                ))}
                            </Box>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, alignSelf: "center", cursor: "help" }}
                          >
                            +{detailedOrganization.o.products.length - 6} more
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}
            </Box>
          </Grid>
        )}

        {/* Key Performance Indicators */}
        {((detailedOrganization?.o?.production_kpis &&
          detailedOrganization.o.production_kpis.length > 0) ||
          (detailedOrganization?.o?.financial_kpis &&
            detailedOrganization.o.financial_kpis.length > 0)) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Key Performance Indicators
              </Typography>

              {/* Production KPIs */}
              {detailedOrganization?.o?.production_kpis &&
                detailedOrganization.o.production_kpis.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Production Metrics:
                    </Typography>
                    <Grid container spacing={2}>
                      {detailedOrganization?.o?.production_kpis
                        .slice(0, 4)
                        .map((kpi, index) => (
                          <Grid item xs={6} key={index}>
                            <Box
                              sx={{
                                p: 2,
                                border: "1px solid #444",
                                borderRadius: 1,
                                backgroundColor: "#1a1a1a",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                              >
                                {kpi.product_or_service}
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="primary.main"
                                gutterBottom
                              >
                                {kpi.capacity_value?.toLocaleString()}{" "}
                                {kpi.capacity_unit}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                gutterBottom
                              >
                                {kpi.reference_period}
                              </Typography>
                              {kpi.summary && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mt: 1,
                                    fontSize: "0.75rem",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {kpi.summary}
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )}

              {/* Financial KPIs */}
              {detailedOrganization?.o?.financial_kpis &&
                detailedOrganization.o.financial_kpis.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Financial Metrics:
                    </Typography>
                    <Grid container spacing={2}>
                      {detailedOrganization.o.financial_kpis
                        .slice(0, 4)
                        .map((kpi, index) => (
                          <Grid item xs={6} key={index}>
                            <Box
                              sx={{
                                p: 2,
                                border: "1px solid #444",
                                borderRadius: 1,
                                backgroundColor: "#1a1a1a",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                              >
                                {kpi.metric_name}
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="primary.main"
                                gutterBottom
                              >
                                {kpi.value?.toLocaleString()} {kpi.unit}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                gutterBottom
                              >
                                {kpi.reference_period}
                              </Typography>
                              {kpi.summary && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mt: 1,
                                    fontSize: "0.75rem",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {kpi.summary}
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )}
            </Box>
          </Grid>
        )}

        {/* Industry Classification */}
        {((detailedOrganization?.o?.naics_codes_with_title?.length ?? 0) > 0 ||
          (detailedOrganization?.o?.input_hs_codes_with_title?.length ?? 0) >
            0 ||
          (detailedOrganization?.o?.output_hs_codes_with_title?.length ?? 0) >
            0) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Industry Classification
              </Typography>
              {/* NAICS Codes */}
              {detailedOrganization?.o?.naics_codes_with_title &&
                detailedOrganization.o.naics_codes_with_title.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      NAICS Industry Codes:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedOrganization.o.naics_codes_with_title
                        .slice(0, 6)
                        .map((code, index) => (
                          <Chip
                            key={index}
                            label={code}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedOrganization.o.naics_codes_with_title.length >
                        6 && (
                        <Tooltip
                          title={
                            <Box>
                              {detailedOrganization.o.naics_codes_with_title
                                .slice(6)
                                .map((code, idx) => (
                                  <div key={idx}>{code}</div>
                                ))}
                            </Box>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, alignSelf: "center", cursor: "help" }}
                          >
                            +
                            {detailedOrganization.o.naics_codes_with_title
                              .length - 6}{" "}
                            more
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}

              {/* HS Codes */}
              <Grid container spacing={3}>
                {detailedOrganization?.o?.input_hs_codes_with_title &&
                  detailedOrganization.o.input_hs_codes_with_title.length >
                    0 && (
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Input HS Codes:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {detailedOrganization.o.input_hs_codes_with_title
                          .slice(0, 4)
                          .map((code, index) => (
                            <Chip
                              key={index}
                              label={code}
                              color="warning"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        {detailedOrganization.o.input_hs_codes_with_title
                          .length > 4 && (
                          <Tooltip
                            title={
                              <Box>
                                {detailedOrganization.o.input_hs_codes_with_title
                                  .slice(4)
                                  .map((code, idx) => (
                                    <div key={idx}>{code}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 1,
                                alignSelf: "center",
                                cursor: "help",
                              }}
                            >
                              +
                              {detailedOrganization.o.input_hs_codes_with_title
                                .length - 4}{" "}
                              more
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  )}
                {detailedOrganization?.o?.output_hs_codes_with_title &&
                  detailedOrganization.o.output_hs_codes_with_title.length >
                    0 && (
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Output HS Codes:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {detailedOrganization.o.output_hs_codes_with_title
                          .slice(0, 4)
                          .map((code, index) => (
                            <Chip
                              key={index}
                              label={code}
                              color="success"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        {detailedOrganization.o.output_hs_codes_with_title
                          .length > 4 && (
                          <Tooltip
                            title={
                              <Box>
                                {detailedOrganization.o.output_hs_codes_with_title
                                  .slice(4)
                                  .map((code, idx) => (
                                    <div key={idx}>{code}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 1,
                                alignSelf: "center",
                                cursor: "help",
                              }}
                            >
                              +
                              {detailedOrganization.o.output_hs_codes_with_title
                                .length - 4}{" "}
                              more
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default OrganizationDetail;
